import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Pill from "../../components/Pill";
import ProfileVisibilityToggle from "../../components/ProfileVisibilityToggle";
import { useFlowActions, useFlowState } from "../../state/FlowProvider";
import { nextStep, ONBOARDING_BASE_PATH } from "../registry";
import { onboardingTokens } from "../../theme";
import { t } from "../../../../locales/t";
import FormSubmitButton from "../../components/FormSubmitButton";
import {
  ETHNICITY_OPTIONS,
  GENDER_OPTIONS,
  PREFER_NOT_TO_SAY,
  SELF_DESCRIBE,
  toggleDemographicValue,
} from "../../../../constants/demographicOptions";

const DEMOGRAPHIC_OPTIONS = {
  gender: GENDER_OPTIONS,
  ethnicity: ETHNICITY_OPTIONS,
  lgbtq: [
    { value: "yes", label: "Yes, I identify as 2SLGBTQ+" },
    { value: "no", label: "No, I do not" },
    { value: "prefer_not_to_say", label: "Prefer not to say" },
  ],
  disability: [
    { value: "yes", label: "Yes, I have a disability" },
    { value: "no", label: "No, I do not" },
    { value: "prefer_not_to_say", label: "Prefer not to say" },
  ],
};

const DemographicQuestion = ({
  id,
  values,
  visible,
  visibilityDisabled,
  onValues,
  onVisible,
}) => {
  const toggle = (option) => {
    if (option === PREFER_NOT_TO_SAY) {
      onValues(values.includes(option) ? [] : [option]);
      return;
    }

    if (id === "lgbtq" || id === "disability") {
      onValues(values.includes(option) ? [] : [option]);
      return;
    }

    onValues(toggleDemographicValue(values, option));
  };

  return (
    <Box
      component="fieldset"
      aria-labelledby={`demographic-${id}-legend`}
      sx={{
        m: 0,
        p: { xs: 2, sm: 2.5 },
        border: `1px solid ${onboardingTokens.track}`,
        borderRadius: onboardingTokens.radius,
        backgroundColor: onboardingTokens.card,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        <Typography
          id={`demographic-${id}-legend`}
          component="legend"
          sx={{ fontWeight: 600, fontSize: "0.9375rem" }}
        >
          {t(`onboarding.demographics.${id}`)}
        </Typography>
        <ProfileVisibilityToggle
          visible={visible}
          disabled={visibilityDisabled}
          onChange={onVisible}
          describedBy={`demographic-${id}-legend`}
        />
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
        {DEMOGRAPHIC_OPTIONS[id].map(({ value, label }) => (
          <Pill
            key={value}
            selected={values.includes(value)}
            fill={onboardingTokens.lightTeal}
            onClick={() => toggle(value)}
          >
            {label}
          </Pill>
        ))}
      </Stack>
    </Box>
  );
};

const DemographicsStep = ({ step }) => {
  const state = useFlowState();
  const { setDemographics } = useFlowActions();
  const navigate = useNavigate();
  const [whyOpen, setWhyOpen] = useState(false);
  const [answers, setAnswers] = useState(() => ({
    ...state.demographics,
    gender: state.demographics?.gender ?? [],
    ethnicity: state.demographics?.ethnicity ?? [],
    lgbtq: state.demographics?.lgbtq ?? [],
    disability: state.demographics?.disability ?? [],
    visibility: {
      gender: false,
      ethnicity: false,
      lgbtq: false,
      disability: false,
      ...state.demographics?.visibility,
    },
  }));

  const updateValues = (id, values) =>
    setAnswers((current) => ({
      ...current,
      [id]: values,
      ...(id === "gender" && !values.includes(SELF_DESCRIBE)
        ? { customGender: "" }
        : {}),
      // Visibility follows the answer (ECH-379). Removing the last value has to
      // take the flag with it, or the member is recorded as displaying an
      // answer they just deleted, which is a privacy defect rather than a
      // cosmetic one.
      visibility: {
        ...current.visibility,
        ...(values.length ? {} : { [id]: false }),
      },
    }));

  /** A dimension can only be shown on the profile once it has something to show. */
  const canBeVisible = (id) => (answers[id] ?? []).length > 0;

  const updateVisibility = (id, visible) =>
    setAnswers((current) => ({
      ...current,
      visibility: {
        ...current.visibility,
        [id]: visible && (current[id] ?? []).length > 0,
      },
    }));

  // Choosing "Another identity" reveals a free-text field. Advancing with it
  // empty records the option and no description, which is worse than not
  // asking (ECH-380). Whitespace is not a description.
  const selfDescribeIncomplete =
    (answers.gender ?? []).includes(SELF_DESCRIBE) &&
    !(answers.customGender ?? "").trim();

  const advance = (demographics) => {
    setDemographics(demographics);
    const next = nextStep(step.id, { ...state, demographics });
    if (next) navigate(`${ONBOARDING_BASE_PATH}/${next.path}`);
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (!selfDescribeIncomplete) advance(answers);
      }}
    >
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
        <Typography variant="h1">
          {t("onboarding.demographics.heading")}
        </Typography>
        <Typography
          sx={{
            px: 1.25,
            py: 0.4,
            borderRadius: 5,
            fontSize: "0.75rem",
            backgroundColor: onboardingTokens.lightTeal,
          }}
        >
          {t("onboarding.demographics.optional")}
        </Typography>
      </Stack>
      <Typography sx={{ color: onboardingTokens.mutedForeground }}>
        {t("onboarding.demographics.body")}
      </Typography>
      <Button
        type="button"
        onClick={() => setWhyOpen(true)}
        sx={{ px: 0, mt: 0.5, mb: 3 }}
      >
        {t("onboarding.demographics.why")}
      </Button>

      <Button
        type="button"
        variant="outlined"
        fullWidth
        onClick={() => advance({})}
        sx={{ mb: 3 }}
      >
        {t("onboarding.demographics.skip")}
      </Button>

      <Stack gap={4}>
        {Object.keys(DEMOGRAPHIC_OPTIONS).map((id) => (
          <Box key={id}>
            <DemographicQuestion
              id={id}
              values={answers[id]}
              visible={answers.visibility[id]}
              visibilityDisabled={!canBeVisible(id)}
              onValues={(values) => updateValues(id, values)}
              onVisible={(visible) => updateVisibility(id, visible)}
            />
            {id === "gender" && answers.gender.includes(SELF_DESCRIBE) && (
              <TextField
                label={t("onboarding.demographics.selfDescribe")}
                placeholder={t(
                  "onboarding.demographics.selfDescribePlaceholder",
                )}
                value={answers.customGender ?? ""}
                onChange={(event) =>
                  setAnswers((current) => ({
                    ...current,
                    customGender: event.target.value,
                  }))
                }
                fullWidth
                sx={{ mt: 2 }}
              />
            )}
          </Box>
        ))}

        <FormSubmitButton
          variant="contained"
          fullWidth
          disabledReason={
            selfDescribeIncomplete
              ? t("onboarding.demographics.selfDescribeRequired")
              : ""
          }
        >
          {t("onboarding.demographics.save")}
        </FormSubmitButton>
      </Stack>

      <Dialog
        open={whyOpen}
        onClose={() => setWhyOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("onboarding.demographics.whyHeading")}</DialogTitle>
        <DialogContent>
          <Typography
            sx={{ color: onboardingTokens.mutedForeground, lineHeight: 1.65 }}
          >
            {t("onboarding.demographics.whyBody")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setWhyOpen(false)}>
            {t("onboarding.demographics.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DemographicsStep;
